'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  Button,
  ConfirmationDialog,
  CustomRolesContent,
  RoleFormModal,
} from '@terra/ui-design-system';
import { Add01Icon } from '@terra/ui-icons';
import type { ApplicationRoleDto } from '@terra/shared-contracts-applications';
import { RoleCopilotPendingEditsSection } from '../../object-context/RoleCopilotPendingEditsSection';
import { useSettingsTabHeaderActions } from '../settingsTabHeaderContext';
import { useTargetCustomRoles } from '../roles/useTargetCustomRoles';
import type { useTargetRoleActions } from './useTargetRoleActions';
import { TargetNoTargetState } from './TargetNoTargetState';

type TargetRolesPanelProps = {
  applicationId: string | undefined;
  hasProjectSelected: boolean;
  isLoading: boolean;
  isReadOnly?: boolean;
  roles: ApplicationRoleDto[];
  onRolesChange: (roles: ApplicationRoleDto[]) => void;
  roleActions: ReturnType<typeof useTargetRoleActions>;
  hasGitIntegration: boolean;
  refetchRoles: () => Promise<unknown>;
  credentials: Array<{ roleId?: string | null }>;
};

type TargetCustomRolesProps = Omit<
  TargetRolesPanelProps,
  'applicationId' | 'hasProjectSelected'
> & { applicationId: string };

function TargetCustomRoles({
  applicationId,
  isLoading,
  isReadOnly,
  roles,
  onRolesChange,
  roleActions,
  hasGitIntegration,
  refetchRoles,
  credentials,
}: TargetCustomRolesProps) {
  const tCommon = useTranslations('designSystem.common');
  const tCustomRoles = useTranslations('designSystem.credentials.customRoles');
  const customRoles = useTargetCustomRoles({
    applicationId,
    roles,
    onRolesChange,
    roleActions,
    refetchRoles,
    credentials,
    isReadOnly,
  });
  const { openCreateRoleModal } = customRoles;
  const hasRoles = roles.length > 0;
  const headerActions = useMemo(
    () =>
      hasRoles ? (
        <Button
          size="xs"
          variant="outline"
          disabled={isReadOnly}
          onClick={openCreateRoleModal}
        >
          <Add01Icon size={16} />
          {tCommon('add')}
        </Button>
      ) : null,
    [hasRoles, isReadOnly, openCreateRoleModal, tCommon],
  );

  useSettingsTabHeaderActions(headerActions);

  return (
    <>
      <CustomRolesContent
        customRoles={roles}
        isLoadingRoles={isLoading}
        isReadOnly={isReadOnly}
        minHeight="340px"
        onAddButtonClick={openCreateRoleModal}
        onEditRoleClick={customRoles.openEditRoleModal}
        onDeleteRoleClick={customRoles.requestDeleteRole}
        onAnalyzeRoleClick={
          hasGitIntegration ? customRoles.analyzeRoleFromList : undefined
        }
      />
      <RoleFormModal
        isOpen={customRoles.isRoleModalOpen}
        onClose={customRoles.closeRoleModal}
        applicationId={applicationId}
        role={customRoles.modalRole}
        hasGitIntegration={hasGitIntegration}
        onSave={customRoles.handleSaveRole}
        onRoleAnalyze={customRoles.analyzeRole}
        formKey={customRoles.modalFormKey}
        headerContent={
          customRoles.activeEditorRoleId ? (
            <RoleCopilotPendingEditsSection
              roleId={customRoles.activeEditorRoleId}
              roleLabel={customRoles.editingRoleLabel}
              {...customRoles.pendingEditHandlers}
            />
          ) : null
        }
      />
      <ConfirmationDialog
        isOpen={!!customRoles.roleToDelete}
        onOpenChange={({ open }) => {
          if (!open) {
            customRoles.clearRoleToDelete();
          }
        }}
        variant="destructive"
        title={tCustomRoles('deleteConfirmTitle')}
        description={tCustomRoles('deleteConfirmDescription')}
        confirmText={tCommon('delete')}
        cancelText={tCommon('cancel')}
        onConfirm={customRoles.confirmDeleteRole}
        loading={customRoles.isDeletingRole}
        showIcon={false}
        maxWidth="498px"
      />
    </>
  );
}

export function TargetRolesPanel({
  applicationId,
  hasProjectSelected,
  ...rolesProps
}: TargetRolesPanelProps) {
  if (!hasProjectSelected || !applicationId) {
    return <TargetNoTargetState descriptionKey="roles" />;
  }

  return <TargetCustomRoles applicationId={applicationId} {...rolesProps} />;
}
